import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { HEIGHT } from 'design-system/Button';
import Indicator from 'design-system/Indicator';
import { CardView } from 'design-system/CardView';
import ListEmptyView from 'design-system/ListEmptyView';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import StockAlert from './StockAlert';
import { ALL_STOCKS_KEY } from '../constants';
import { isWeb } from '../../../constants';
import { getTriggeredStockAlerts } from '../actions';
import { selectIsInitialRequestingTriggeredStockAlerts, selectIsRequestingTriggeredStockAlerts, } from '../reducers/selectors';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.alertId;
const TriggeredStockAlerts = ({ assetId, scrollY }) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const styles = useStyles(styleSet);
    const shouldLoadSecondPage = useRef(false);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle, styles.flashList);
    const loading = useAppSelector((store) => selectIsRequestingTriggeredStockAlerts(store, assetId));
    const initialRequestingTriggeredStockAlerts = useAppSelector((store) => selectIsInitialRequestingTriggeredStockAlerts(store, assetId));
    const triggeredStockAlerts = useAppSelector((state) => state.unpersistedInvestReducer.triggeredStockAlerts?.[assetId || ALL_STOCKS_KEY]);
    useEffect(() => {
        if (!initialRequestingTriggeredStockAlerts) {
            dispatch(getTriggeredStockAlerts(assetId));
        }
    }, [assetId]);
    const loadMoreData = useCallback(() => {
        const token = store.getState().unpersistedInvestReducer.triggeredStockAlertsPaginationToken;
        if (token) {
            dispatch(getTriggeredStockAlerts(assetId, token));
        }
    }, [assetId]);
    useEffect(() => {
        if (!loading && shouldLoadSecondPage.current) {
            shouldLoadSecondPage.current = false;
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const onLoadNextPage = useCallback(() => {
        if (loading) {
            shouldLoadSecondPage.current = true;
        }
        else {
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const ListEmptyComponent = useMemo(() => {
        if (initialRequestingTriggeredStockAlerts) {
            return (<CardView noMarginTop noMarginBottom style={styles.loaderCard}>
          <Indicator dark/>
        </CardView>);
        }
        return <ListEmptyView onCard title="Nothing to show"/>;
    }, [initialRequestingTriggeredStockAlerts, styles.loaderCard]);
    const itemsLength = triggeredStockAlerts?.length || 0;
    const renderItem = useCallback(({ item, index }) => (<StockAlert index={index} key={item.alertId} style={styles.card} symbol={item.symbol} alertId={item.alertId} assetId={item.assetId} itemsLength={itemsLength} triggeredAt={item.triggeredAt} alertPriceValue={item.targetPrice.value} alertPriceCurrency={item.targetPrice.currency}/>), [itemsLength, styles.card]);
    return (<AnimatedFlashList extraData={styles.card} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} data={triggeredStockAlerts} keyExtractor={keyExtractor} keyboardDismissMode="on-drag" onEndReached={onLoadNextPage} keyboardShouldPersistTaps="handled" contentContainerStyle={paddingBottom} ListEmptyComponent={ListEmptyComponent} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>);
};
export default TriggeredStockAlerts;
const styleSet = createStyleSheets((colors) => ({
    flashList: {
        paddingTop: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    loaderCard: {
        padding: rem(156),
    },
}));
